<script setup lang="ts">
import { computed, type VNode } from 'vue';

import { useDataTestid, useSlotText } from '@/kasten-components/composables';
import { cnMerge } from '@/utils/utils.js';

import { badgeVariants } from './badge-variants';
import KaBadgeRemove from './KaBadgeRemove.vue';
import type { KaBadgeProps } from './types';
export type { KaBadgeProps } from './types';

const props = withDefaults(defineProps<KaBadgeProps>(), {
  label: undefined,
  background: 'transparent',
  color: 'gray',
  removeable: false,
  class: '',
  iconContainerClasses: '',
  removeButtonClasses: '',
});

const emit = defineEmits<{
  (e: 'remove:click', event: MouseEvent): void;
  (e: 'remove:space', event: KeyboardEvent): void;
  (e: 'remove:enter', event: KeyboardEvent): void;
  (e: 'remove:focus', event: FocusEvent): void;
  (e: 'remove:blur', event: FocusEvent): void;
}>();

defineSlots<{
  default?(props: { label?: string }): VNode | VNode[];
  left?(props: Record<string, never>): VNode | VNode[];
  remove?(props: Record<string, never>): VNode | VNode[];
}>();

const { dataTestid, suffix } = useDataTestid();

const slotTextContent = useSlotText({
  name: 'default',
  slotProps: { label: props.label },
});
const internalLabel = computed(() => props.label || slotTextContent.value);
</script>

<template>
  <span
    :data-testid="dataTestid"
    :class="
      cnMerge(
        badgeVariants({
          color: props.color,
          background: props.background,
        }),
        props.class,
      )
    "
  >
    <span
      v-if="$slots.left || props.left"
      :data-testid="suffix('icon')"
      :class="cnMerge('size-3 flex-none', props.iconContainerClasses)"
      aria-hidden="true"
    >
      <slot name="left">
        <component
          :is="props.left.icon"
          v-if="props.left"
          :class="cnMerge('block h-full w-full', props.left?.classes)"
          tabindex="-1"
        />
      </slot>
    </span>

    <slot :label="props.label">{{ props.label }}</slot>

    <slot name="remove">
      <KaBadgeRemove
        v-if="removeable"
        :data-testid="suffix('remove-button')"
        :label="internalLabel"
        :color="props.color"
        @remove:click.stop="emit('remove:click', $event)"
        @remove:space.stop="emit('remove:space', $event)"
        @remove:enter.stop="emit('remove:enter', $event)"
        @remove:focus="emit('remove:focus', $event)"
        @remove:blur="emit('remove:blur', $event)"
      />
    </slot>
  </span>
</template>
